import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { fetchPortfolioJson } from '@/lib/fetchers/portfolio-json';
import { mergePortfolioPreservingCachedSeats } from '@/lib/drawer-utils';
import { fetchUserProfileJson } from '@/hooks/use-user-profile';
import { fetchWithTimeout } from '@/lib/fetch-with-timeout';
import {
  PROFILE_ACTIVITY_QUERY_KEY,
  fetchProfileActivityPage,
} from '@/components/profile/profile-activity-feed';

async function fetchCreatorGrids(addr: string) {
  const res = await fetchWithTimeout(`/api/profile/${encodeURIComponent(addr)}/grids`, {
    timeoutMs: 60_000,
  });
  if (!res.ok) return { data: [] };
  return res.json();
}

async function fetchUserPlinth(addr: string) {
  const r = await fetchWithTimeout(`/api/users/${encodeURIComponent(addr)}/plinth`, {
    timeoutMs: 20_000,
  });
  if (!r.ok) return { plinthId: null as string | null };
  const j = await r.json();
  return j.data as { plinthId: string | null };
}

/**
 * Warm React Query for everything the embedded/full profile UI needs, in parallel.
 */
export function prefetchProfileModalBundle(queryClient: QueryClient, address: string): Promise<
  PromiseSettledResult<unknown>[]
> {
  const normalized = address.toLowerCase();
  return Promise.allSettled([
    queryClient.prefetchQuery({
      queryKey: queryKeys.portfolio(address),
      queryFn: async () => {
        const fresh = await fetchPortfolioJson(address);
        const cached = queryClient.getQueryData(queryKeys.portfolio(address));
        return mergePortfolioPreservingCachedSeats(cached, fresh);
      },
      staleTime: 30_000,
    }),
    queryClient.prefetchQuery({
      queryKey: ['user-profile', normalized],
      queryFn: () => fetchUserProfileJson(address),
      staleTime: 30_000,
    }),
    queryClient.prefetchQuery({
      queryKey: ['creator-grids', normalized],
      queryFn: () => fetchCreatorGrids(normalized),
      staleTime: 30_000,
    }),
    // Use `prefetchInfiniteQuery` so the cache shape matches the consumer
    // (`useInfiniteQuery` in `ProfileActivityFeed`). Mixing the two on the
    // same key would leave React Query with a non-paginated entry and
    // force a refetch on first mount, defeating the prewarm.
    queryClient.prefetchInfiniteQuery({
      queryKey: PROFILE_ACTIVITY_QUERY_KEY(normalized),
      queryFn: () => fetchProfileActivityPage(normalized),
      initialPageParam: undefined as number | undefined,
      staleTime: 20_000,
    }),
    queryClient.prefetchQuery({
      queryKey: queryKeys.userPlinth(address),
      queryFn: () => fetchUserPlinth(normalized),
      staleTime: 60_000,
    }),
  ]);
}
